import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { DropdownMenu } from 'radix-ui';
import { Button } from 'components/common';

import type { ButtonInterface } from 'components/common/Button';

interface Props {
  children:
    | React.ReactNode
    | (({
        setRootIsOpen
      }: {
        setRootIsOpen: (isOpen: boolean) => void;
      }) => React.ReactNode);
  contentSideOffset?: number;
  contentTopOffset?: number;
  numberOfActiveFilters: number;
  onClear: () => void;
  onSubmit: () => void;
  portalId?: string;
  trigger?: React.ReactElement<any>;
  triggerProps?: ButtonInterface;
}

const FilterButtonRoot = ({
  children,
  contentSideOffset = 0,
  contentTopOffset = 0,
  numberOfActiveFilters,
  onClear,
  onSubmit,
  portalId = 'filters-root',
  trigger,
  triggerProps
}: Props) => {
  const { messages, theme } = useReduxState();

  const portalContainer = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    portalContainer.current = document.createElement('div');
    portalContainer.current.setAttribute('id', portalId);

    const modalRoot = document.querySelector('.modal-root') as HTMLDivElement;
    modalRoot.appendChild(portalContainer.current);

    return () => {
      modalRoot.removeChild(portalContainer.current);
    };
  }, []);

  const handleClear = () => {
    if (onClear) onClear();
    setIsOpen(false);
  };

  const handleApply = () => {
    if (onSubmit) onSubmit();
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onSubmit();
    setIsOpen(open);
  };

  return (
    <div style={{ alignItems: 'flex-start', display: 'inline-flex', gap: 1 }}>
      <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenu.Trigger asChild>
          {trigger ? (
            trigger
          ) : (
            <Button
              category={numberOfActiveFilters > 0 ? 'group' : 'lighter'}
              icon="filter"
              iconStyle={{ fontSize: '1rem', marginRight: 5 }}
              size="header"
              text={
                <div className="filters-text">
                  {triggerProps?.text || messages.commonFilters}

                  {numberOfActiveFilters > 0 ? (
                    <div className="filters-count">{numberOfActiveFilters}</div>
                  ) : null}
                </div>
              }
              {...Object.keys(triggerProps || {}).reduce((props, key) => {
                if (key !== 'text') props[key] = triggerProps[key];
                return props;
              }, {})}
            />
          )}
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal container={portalContainer.current}>
          <DropdownMenu.Content
            align="start"
            alignOffset={contentSideOffset}
            className="vuo-filters-content"
            hideWhenDetached={true}
            loop={true}
            sideOffset={contentTopOffset}
          >
            <div className="content">
              {typeof children === 'function'
                ? children({
                    setRootIsOpen: (isOpen) => setIsOpen(isOpen)
                  })
                : children}
            </div>

            <div className="footer">
              <DropdownMenu.Item
                asChild={true}
                onPointerMove={(ev) => ev.preventDefault()}
                onPointerLeave={(ev) => ev.preventDefault()}
              >
                <Button
                  category="lighter"
                  onClick={handleClear}
                  size="small"
                  text={messages.commonClearAll}
                />
              </DropdownMenu.Item>

              <DropdownMenu.Item
                asChild={true}
                onPointerMove={(ev) => ev.preventDefault()}
                onPointerLeave={(ev) => ev.preventDefault()}
              >
                <Button
                  category="success"
                  onClick={handleApply}
                  size="small"
                  text={messages.filtersApply}
                />
              </DropdownMenu.Item>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {!trigger && numberOfActiveFilters > 0 ? (
        <Button
          category="group"
          icon="times"
          onClick={onClear}
          size="header"
          tooltip={messages.commonClear}
        />
      ) : null}

      <style jsx>{`
        .filters-text {
          align-items: center;
          display: inline-flex;
          gap: 7px;
        }
        .filters-count {
          background-color: white;
          border-radius: 20px;
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 700;
          margin: -2px 0;
          padding: 2px 0;
          text-align: center;
          height: 20px;
          width: 20px;
        }
        .content {
          overflow: auto;
        }
        .footer {
          align-items: center;
          border-top: 1px solid ${theme.lightGreyDarkActive};
          display: flex;
          flex-grow: 1;
          gap: 10px;
          justify-content: space-between;
          padding: 10px;
          width: 100%;
        }
      `}</style>

      <style jsx global>{`
        .vuo-filters-content {
          background-color: white;
          border-radius: ${theme.dropdownBorderRadius};
          box-shadow: ${theme.dropdownShadow};
          display: flex;
          flex-direction: column;
          max-height: calc(
            max(
                min(var(--radix-dropdown-menu-content-available-height), 500px),
                200px
              ) - 20px
          );
          max-width: 300px;
          min-width: 240px;
          z-index: 30;

          animation-name: vuoFilterButtonRootReveal;
          animation-duration: 200ms;
          animation-timing-function: cubic-bezier(0.35, 0, 0.25, 1);
          will-change: height, opacity, overflow, transform, visibility;
        }

        @keyframes vuoFilterButtonRootReveal {
          from {
            height: 0;
            opacity: 0;
            overflow: hidden;
            transform: scale(0.8);
            transform-origin: center top 0;
            transition: all 0.2s cubic-bezier(0.35, 0, 0.25, 1);
            visibility: hidden;
          }
          to {
            height: auto;
            opacity: 1;
            overflow: visible;
            transform: none;
            visibility: visible;
          }
        }
      `}</style>
    </div>
  );
};

export default FilterButtonRoot;
